import React, { useState, useEffect } from "react";
import { useMeta } from "@/hooks/useMeta";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const now = new Date();
const CUR_MONTH = now.toISOString().slice(0, 7);
const CUR_YEAR = String(now.getFullYear());
const BULAN_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
// Last 24 months as {value, label} — replaces a bare <input type="month">, whose native
// picker renders very differently per browser/OS (cramped on desktop Chrome, a full-screen
// wheel that breaks the dark theme on iOS Safari) and was the "dropdown bulan cacat" report.
const MONTH_OPTS = Array.from({ length: 24 }, (_, i) => {
  const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
  const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  return { value, label: `${BULAN_ID[d.getMonth()]} ${d.getFullYear()}` };
});

export default function FilterBar({ onChange }) {
  const { user } = useAuth();
  const { branches, salesUsers } = useMeta();
  const [mode, setMode] = useState("all");
  const [month, setMonth] = useState(CUR_MONTH);
  const [year, setYear] = useState(CUR_YEAR);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [branch, setBranch] = useState("all");
  const [sales, setSales] = useState("all");
  const isSales = user?.role === "sales";

  useEffect(() => {
    const p = new URLSearchParams();
    if (mode === "month") p.set("month", month);
    else if (mode === "year") p.set("year", year);
    else if (mode === "range") { if (start) p.set("start", start); if (end) p.set("end", end); }
    if (!isSales && branch !== "all") p.set("branch_id", branch);
    if (!isSales && sales !== "all") p.set("sales_id", sales);
    onChange(p.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, month, year, start, end, branch, sales]);

  return (
    <div className="card-surface rounded-xl p-2 lg:p-1.5 mb-4 flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-1.5">
      <div className="flex gap-1.5 overflow-x-auto hide-scrollbar shrink-0">
        {[["all", "Semua"], ["month", "Bulan"], ["year", "Tahun"], ["range", "Rentang"]].map(([v, l]) => (
          <button key={v} data-testid={`period-${v}`} onClick={() => setMode(v)}
            className={`px-2.5 py-1 rounded-md text-[11px] lg:text-xs font-medium whitespace-nowrap transition-colors ${mode === v ? "bg-blue-500/20 text-blue-300 border border-blue-500/30" : "bg-[#18181B] text-zinc-400 border border-white/5"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {mode === "month" && (
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="filter-month" className="bg-[#18181B] border-white/10 h-7 text-xs w-36"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#18181B] border-white/10">{MONTH_OPTS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {mode === "year" && (
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="bg-[#18181B] border-white/10 h-7 text-xs w-24"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#18181B] border-white/10">{[0, 1, 2].map((i) => { const y = String(now.getFullYear() - i); return <SelectItem key={y} value={y}>{y}</SelectItem>; })}</SelectContent>
          </Select>
        )}
        {mode === "range" && (<>
          <Input data-testid="filter-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-[#18181B] border-white/10 h-7 text-xs w-36" />
          <Input data-testid="filter-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-[#18181B] border-white/10 h-7 text-xs w-36" />
        </>)}
        {!isSales && (
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger className="bg-[#18181B] border-white/10 h-7 text-xs w-32"><SelectValue placeholder="Cabang" /></SelectTrigger>
            <SelectContent className="bg-[#18181B] border-white/10"><SelectItem value="all">Semua Cabang</SelectItem>{branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {!isSales && (
          <Select value={sales} onValueChange={setSales}>
            <SelectTrigger className="bg-[#18181B] border-white/10 h-7 text-xs w-32"><SelectValue placeholder="Sales" /></SelectTrigger>
            <SelectContent className="bg-[#18181B] border-white/10"><SelectItem value="all">Semua Sales</SelectItem>{salesUsers.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}
